import { IonIcon } from '@ionic/react';
import {
  alertCircleOutline,
  buildOutline,
  bulbOutline,
  personCircleOutline,
  refreshOutline,
  sparklesOutline,
} from 'ionicons/icons';
import React, { useEffect, useRef } from 'react';
import { useIcons } from '../../hooks/useIcons';
import { presentThread } from './AgentChatActivity';
import {
  AgentChatMessage,
  AgentMessageRole,
  AgentRun,
} from './AgentChat.types';
import { ChatEngine, missingChatKeyHint } from './agentEngine';
import { toUserFacingChatError } from './chatUserError';
import { stripToolNamesFromReply } from './chatReplySanitize';
import AgentChatMessageBody from './AgentChatMessageBody';
import AgentReplyFeedback from './AgentReplyFeedback';
import AgentUserMessageBody from './AgentUserMessageBody';
import DuplicateStatsCard from './DuplicateStatsCard';
import StreamingTransferCard from './StreamingTransferCard';

interface AgentChatThreadProps {
  run: AgentRun | null;
  /** Null while the main process is still being asked whether it has a key. */
  configured: boolean | null;
  engine?: ChatEngine;
  /** Starts a new conversation with this prompt. */
  onRerunPrompt?: (prompt: string) => void;
}

/** Near enough to the bottom still counts as following the reply. */
const FOLLOW_THRESHOLD_PX = 64;

const ROLE_ICONS: Record<AgentMessageRole, string> = {
  user: personCircleOutline,
  agent: sparklesOutline,
  thinking: bulbOutline,
  tool: buildOutline,
};

const EmptyState: React.FC<{
  configured: boolean | null;
  engine?: ChatEngine;
}> = ({ configured, engine }) => {
  const { icons } = useIcons();
  return (
    <div className="agent-thread-empty">
      {icons.flameLogo ? (
        <img
          src={icons.flameLogo}
          alt=""
          className="agent-thread-empty-brand"
        />
      ) : (
        <IonIcon icon={sparklesOutline} className="agent-thread-empty-icon" />
      )}
      <h4>What are we working on?</h4>
      <p>
        Ask about Crate Hackers crates, your library, or a set for tonight —
        what you have, what is worth cleaning up, or something to play.
      </p>
      {configured === false && (
        <p className="agent-thread-empty-warning">
          <IonIcon icon={alertCircleOutline} />
          {missingChatKeyHint(engine)}
        </p>
      )}
    </div>
  );
};

const friendlyRunError = (text: string): string => toUserFacingChatError(text);

const lastAgentMessage = (
  messages: AgentChatMessage[],
): AgentChatMessage | null => {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === 'agent') return messages[index];
  }
  return null;
};

const isStreamingAgentMessage = (
  message: AgentChatMessage,
  run: AgentRun,
): boolean => {
  if (run.status !== 'queued' && run.status !== 'running') return false;
  if (message.role !== 'agent') return false;
  return lastAgentMessage(run.messages)?.id === message.id;
};

const messageBody = (
  message: AgentChatMessage,
  run: AgentRun,
  hideDuplicateCleanerLink: boolean,
) => {
  const raw = /^run error/i.test(message.content)
    ? friendlyRunError(message.content)
    : message.content;
  const content = message.role === 'agent' ? stripToolNamesFromReply(raw) : raw;
  if (message.role === 'agent') {
    return (
      <AgentChatMessageBody
        content={content}
        streaming={isStreamingAgentMessage(message, run)}
        hideDuplicateCleanerLink={hideDuplicateCleanerLink}
        runId={run.id}
        busy={run.status === 'queued' || run.status === 'running'}
      />
    );
  }
  if (message.role === 'user') {
    return (
      <AgentUserMessageBody
        content={content}
        attachments={message.attachments}
      />
    );
  }
  return content;
};

const MessageAvatar: React.FC<{ role: AgentMessageRole }> = ({ role }) => (
  <div className="agent-message-avatar">
    <IonIcon icon={ROLE_ICONS[role] ?? sparklesOutline} />
  </div>
);

export const AgentChatThread: React.FC<AgentChatThreadProps> = ({
  run,
  configured,
  engine,
  onRerunPrompt,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Whether the view should keep following the reply as it streams in. */
  const followRef = useRef(true);

  // The last bubble grows as text streams in, so message count alone would not
  // change and the view would stop following along.
  const streamedLength =
    run?.messages.reduce((total, entry) => total + entry.content.length, 0) ??
    0;

  // Reading back through a reply has to survive the next token arriving, so
  // scrolling away from the bottom stops the view following until they return.
  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    followRef.current = fromBottom <= FOLLOW_THRESHOLD_PX;
  };

  // A different conversation starts at its newest message.
  useEffect(() => {
    followRef.current = true;
  }, [run?.id]);

  const lastRole = run?.messages[run.messages.length - 1]?.role;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Sending is a deliberate move back to the live end of the thread.
    if (lastRole === 'user') followRef.current = true;
    if (followRef.current) el.scrollTop = el.scrollHeight;
  }, [run?.id, run?.messages.length, streamedLength, run?.status, lastRole]);

  if (!run) {
    return (
      <div className="agent-thread" ref={scrollRef} onScroll={onScroll}>
        <EmptyState configured={configured} engine={engine} />
      </div>
    );
  }

  const awaitingAgent = run.status === 'queued' || run.status === 'running';
  const lastMessage = run.messages[run.messages.length - 1];
  const showTyping =
    awaitingAgent &&
    !(lastMessage?.role === 'agent' && lastMessage.content.length > 0);
  const items = presentThread(run.messages, run).filter(
    (item) =>
      !(
        item.type === 'message' &&
        run.status === 'failed' &&
        run.error &&
        item.message.role === 'tool' &&
        /^run error/i.test(item.message.content)
      ),
  );

  const renderAgentExtras = (message: AgentChatMessage) =>
    message.duplicateInsights ? (
      <DuplicateStatsCard insights={message.duplicateInsights} />
    ) : null;

  const stamp = run.messages[0]?.createdAt || run.createdAt;
  const stampDate = new Date(stamp);
  const sameDay = new Date().toDateString() === stampDate.toDateString();
  let stampLabel = '';
  try {
    const time = stampDate.toLocaleTimeString([], {
      hour: 'numeric',
      minute: '2-digit',
    });
    stampLabel = sameDay
      ? `Today · ${time}`
      : `${stampDate.toLocaleDateString([], {
          month: 'short',
          day: 'numeric',
        })} · ${time}`;
  } catch {
    stampLabel = stampDate.toDateString();
  }

  return (
    <div className="agent-thread" ref={scrollRef} onScroll={onScroll}>
      <div className="agent-session-divider">
        <span>{stampLabel}</span>
      </div>
      {items.map((item) =>
        item.type === 'activity' ? (
          <div
            key={item.id}
            className="agent-message agent-message-tool agent-message-activity"
          >
            <MessageAvatar role="tool" />
            <div className="agent-message-body">
              {`${item.pending ? '•' : item.failed ? '✗' : '✓'} ${item.text}`}
            </div>
          </div>
        ) : (
          <div
            key={item.message.id}
            className={`agent-message agent-message-${item.message.role}`}
          >
            <MessageAvatar role={item.message.role} />
            {item.message.role === 'user' && onRerunPrompt ? (
              <div className="agent-message-body">
                <span className="agent-message-text">
                  {messageBody(item.message, run, false)}
                </span>
                <button
                  type="button"
                  className="agent-message-rerun"
                  title="Rerun as a new chat"
                  onClick={() => onRerunPrompt(item.message.content)}
                >
                  <IonIcon icon={refreshOutline} />
                  Rerun
                </button>
              </div>
            ) : (
              <div className="agent-message-body">
                {messageBody(
                  item.message,
                  run,
                  Boolean(item.message.duplicateInsights),
                )}
                {renderAgentExtras(item.message)}
                <AgentReplyFeedback run={run} message={item.message} />
              </div>
            )}
          </div>
        ),
      )}

      <StreamingTransferCard />

      {run.status === 'failed' && run.error && (
        <div className="agent-message agent-message-error">
          <div className="agent-message-avatar">
            <IonIcon icon={alertCircleOutline} />
          </div>
          <div className="agent-message-body">
            {friendlyRunError(run.error)}
          </div>
        </div>
      )}

      {showTyping && (
        <div className="agent-message agent-message-agent">
          <MessageAvatar role="agent" />
          <div className="agent-message-body agent-message-pending">
            <span className="agent-typing">
              <i />
              <i />
              <i />
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentChatThread;
